import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { CheckCircle, ArrowLeft, PenLine } from 'lucide-react';

const BudgetWinSignOn = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [count, setCount] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    organization: '',
    title: '',
    email: ''
  });

  useEffect(() => {
    fetch(`${API_URL}/api/sign-ons/budget-win-thank-you/count`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCount(d.count))
      .catch(() => {});
  }, [API_URL]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.organization.trim() || !form.email.trim()) {
      toast.error('Please fill in your name, organization, and email');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/sign-ons/budget-win-thank-you/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (response.ok) {
        setIsComplete(true);
        setCount(c => (c == null ? 1 : c + 1));
        toast.success('You\u2019re signed on! Thank you.');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Submission failed. Please try again.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="main-content" role="main" tabIndex={-1} className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0f213a] to-[#0a1628] text-white" data-testid="budget-win-signon-page">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-2">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-400 hover:text-amber-400 text-sm transition-colors"
          data-testid="signon-back-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to dcelc.org
        </button>
      </div>

      {/* Hero */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-6 text-center">
        <span className="inline-block px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/40 text-amber-300 text-[11px] font-bold tracking-[0.2em] uppercase">
          🏆 Victory · Sign-On Letter
        </span>
        <h1 className="mt-5 text-4xl sm:text-5xl font-black text-amber-400 leading-tight" style={{ fontFamily: "'Helvetica Neue', Arial Black, sans-serif" }}>
          Sign on to our<br />Thank-You Letter
        </h1>
        <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
          DCELC is sending an official letter to the DC Council and Chairman Mendelson thanking them for restoring child care funding in the proposed FY27 budget. <strong className="text-white">Add your name and your organization</strong> to amplify the field&apos;s gratitude.
        </p>
        {count !== null && (
          <p className="mt-4 text-amber-300 text-sm tracking-wide">
            <strong>{count.toLocaleString('en-US')}</strong> {count === 1 ? 'person has' : 'people have'} signed on so far
          </p>
        )}
      </div>

      {/* Form / Success */}
      <div className="max-w-xl mx-auto px-4 sm:px-6 pb-16">
        {isComplete ? (
          <div className="bg-white text-[#0a1628] rounded-2xl shadow-2xl p-9 text-center" data-testid="signon-complete">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <CheckCircle className="w-9 h-9 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold mb-3">You&apos;re signed on!</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              Thank you, <strong>{form.full_name}</strong>. <strong>{form.organization}</strong> has been added to the DCELC thank-you letter. We&apos;ll send the compiled letter once we close sign-ons.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => { setIsComplete(false); setForm({ full_name: '', organization: '', title: '', email: '' }); }}
                variant="outline"
                data-testid="signon-add-another"
              >
                Add another signer
              </Button>
              <Button
                onClick={() => navigate('/')}
                className="bg-amber-500 hover:bg-amber-600 text-[#0a1628] font-bold"
                data-testid="signon-back-button"
              >
                Back to DCELC
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-white text-[#0a1628] rounded-2xl shadow-2xl p-7 sm:p-9 space-y-4"
            data-testid="signon-form"
          >
            <div className="flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-amber-600 pb-1">
              <PenLine className="w-4 h-4" />
              Add Your Name
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Full Name <span className="text-rose-500">*</span></label>
              <Input
                name="full_name"
                value={form.full_name}
                onChange={handleChange}
                required
                placeholder="Your full name"
                data-testid="signon-full-name"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Organizational Affiliation <span className="text-rose-500">*</span></label>
              <Input
                name="organization"
                value={form.organization}
                onChange={handleChange}
                required
                placeholder="Your organization, program, or business"
                data-testid="signon-organization"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Title / Role <span className="text-slate-400 font-normal">(optional)</span></label>
              <Input
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g., Executive Director, Lead Teacher, Parent Advocate"
                data-testid="signon-title"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Email <span className="text-rose-500">*</span></label>
              <Input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder="you@example.com"
                data-testid="signon-email"
              />
              <p className="text-[11px] text-slate-500 mt-1">We&apos;ll only use your email to send a confirmation and share the final letter.</p>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-500 hover:bg-amber-600 text-[#0a1628] font-black tracking-wide text-base py-6 shadow-lg shadow-amber-500/30"
                data-testid="signon-submit"
              >
                {isSubmitting ? 'Signing on\u2026' : 'Sign the Thank-You Letter \u2192'}
              </Button>
            </div>

            <p className="text-[11px] text-slate-400 text-center pt-1">
              By signing on, you consent to your name and organization being listed in the final letter to the DC Council.
            </p>
          </form>
        )}
      </div>

      {/* Footer */}
      <div className="max-w-3xl mx-auto px-4 pb-10 text-center text-slate-500 text-xs">
        <p><strong className="text-amber-400">DC Early Learning Collaborative</strong></p>
        <p className="mt-1">Brought to you by the field, for the field. #KeepDCsChildCarePromise</p>
      </div>
    </div>
  );
};

export default BudgetWinSignOn;
